import React, { useEffect, useState } from 'react';
import AdminLayout from '../../../components/layout/AdminLayout';
import Card from '../../../components/common/Card';
import MemberTable from '../../../components/tables/MemberTable';
import Button from '../../../components/common/Button';
import FormField from '../../../components/common/FormField';
import {
  AuthClient,
  AuthDTO,
  ImpersonationDTO,
  MemberClient,
  MemberDTO,
  SearchRequestDTO_memberSearchRequestDTO,
} from '../../../services/apiClient';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../../../config/config';
import axiosInstance from '../../../services/axiosInstance';
import AddNewButton from '../../../components/common/AddButton';
import { confirmDelete } from '../../../utils/sweetAlertUtils';
import { getUserId } from '../../../utils/tokenUtils';

const MemberList: React.FC = () => {
  const [items, setItems] = useState<MemberDTO[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const [firstNameSearchTerm, setFirstNameSearchTerm] = useState('');
  const [lastNameSearchTerm, setLastNameSearchTerm] = useState('');
  const [membershipNumberSearchTerm, setMembershipNumberSearchTerm] =
    useState('');
  const [emailSearchTerm, setEmailSearchTerm] = useState('');

  const navigate = useNavigate();
  const breadcrumbs = [
    { label: 'Admin', href: '#' },
    { label: 'Members', href: '#' },
  ];

  const apiClient = new MemberClient(API_BASE_URL, axiosInstance);
  const impersonateClient = new AuthClient(API_BASE_URL, axiosInstance);

  const fetchItems = async (page: number, size: number, filters = {}) => {
    const searchRequest: SearchRequestDTO_memberSearchRequestDTO = {
      filters: {
        firstName: firstNameSearchTerm || '',
        lastName: lastNameSearchTerm || '',
        membershipNumber: membershipNumberSearchTerm || '',
        email: emailSearchTerm || '',
        ...filters,
      },
      page,
      pageSize: size,
      sortBy: 'CreatedUtc',
      sortDescending: true,
    };

    try {
      setIsLoading(true);
      const response = await apiClient.searchMembers(searchRequest);
      const totalRecords = response.data?.totalCount ?? 0;

      setItems((response.data?.results as MemberDTO[]) || []);
      setTotalRecords(totalRecords);
      setTotalPages(Math.ceil(totalRecords / size));
    } catch (error) {
      console.error(`Error fetching Members:`, error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchItems(currentPage, pageSize);
  }, [currentPage, pageSize]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchItems(1, pageSize);
  };

  const handleClearSearch = () => {
    setFirstNameSearchTerm('');
    setLastNameSearchTerm('');
    setMembershipNumberSearchTerm('');
    setEmailSearchTerm('');
    fetchItems(1, pageSize, {
      firstName: '',
      lastName: '',
      email: '',
      membershipNumber: '',
    });
  };

  const handleDelete = async (dto: MemberDTO) => {
    const confirmed = await confirmDelete();
    if (!confirmed) return;

    try {
      await apiClient.deleteMember(dto.id || '');
      await fetchItems(currentPage, pageSize, {});
    } catch (error) {
      console.error('Error deleting:', error);
    }
  };

  const handleImpersonateLogin = async (dto: MemberDTO) => {

    const impersonateDto: ImpersonationDTO = {
      memberId: dto.userID,
      adminId: getUserId() ?? ""
    }

    try {
      const response = await impersonateClient.impersonateMemberLogin(impersonateDto);

      if (response && response.success && response.data) {
        const responseData = response.data as AuthDTO;

        localStorage.setItem('token', responseData.accessToken ?? '');
        localStorage.setItem('refresh_token', responseData.refreshToken ?? '',
        );
        navigate('/admin/dashboard');
      }
    } catch (error) {
      console.error('Error impersonating login as member', error);
    } finally {

    }
  }

  return (
    <AdminLayout
      activeMenuItem="/admin/members"

      breadcrumbs={breadcrumbs}
      allowedRoles={['Administrator']}
    >
      <Card title="Members">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-gray-500 dark:text-gray-300">
            Manage Members ({totalRecords})
          </h2>
          <div className="ml-auto">
            <AddNewButton onClick={() => navigate('/admin/members/create')} />
          </div>
        </div>

        <form onSubmit={handleSearch} className="mb-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            <FormField
              id="firstNameSearchTerm"
              label="First Name"
              value={firstNameSearchTerm}
              onChange={(e) => setFirstNameSearchTerm(e.target.value)}
              placeholder="Search by first name"
            />
            <FormField
              id="lastNameSearchTerm"
              label="Last Name"
              value={lastNameSearchTerm}
              onChange={(e) => setLastNameSearchTerm(e.target.value)}
              placeholder="Search by last name"
            />
            <FormField
              id="membershipNumberSearchTerm"
              label="Membership Number"
              value={membershipNumberSearchTerm}
              onChange={(e) => setMembershipNumberSearchTerm(e.target.value)}
              placeholder="Search by membership number"
            />
            <FormField
              id="emailSearchTerm"
              label="Email"
              value={emailSearchTerm}
              onChange={(e) => setEmailSearchTerm(e.target.value)}
              placeholder="Search by email"
            />
          </div>

          <div className="flex space-x-4 mb-4">
            <Button variant="secondary" onClick={handleClearSearch}>
              Clear Search
            </Button>
            <Button type="submit">Search</Button>
          </div>
        </form>

        <MemberTable
          data={items}
          handleEdit={(dto) => navigate(`/admin/members/${dto.id}`)}
          handleDelete={handleDelete}
          handleImpersonateLogin={handleImpersonateLogin}
          currentPage={currentPage}
          totalPages={totalPages}
          setCurrentPage={setCurrentPage}
          pageSize={pageSize}
          setPageSize={setPageSize}
          isLoading={isLoading}
        />
      </Card>
    </AdminLayout>
  );
};

export default MemberList;
